<template>
  <el-dialog
    :model-value="modelValue"
    width="560px"
    append-to-body
    destroy-on-close
    align-center
    class="project-overview-select-dialog"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <template #header>
      <div class="select-header">
        <h3 class="select-title">选择项目</h3>
        <p class="select-subtitle">选择您关注的项目进行汇总统计</p>
      </div>
    </template>

    <div class="select-toolbar">
      <el-checkbox
        :model-value="allChecked"
        :indeterminate="indeterminate"
        @change="onToggleAll"
      >
        全选
      </el-checkbox>
      <span class="select-count">已选 {{ draftIds.length }} 个项目</span>
    </div>

    <el-table
      ref="tableRef"
      :data="projects"
      class="select-table"
      height="360"
      row-key="id"
      :row-class-name="rowClassName"
      @row-click="onRowClick"
    >
      <el-table-column width="56" align="center">
        <template #header>
          <span class="col-head">选择</span>
        </template>
        <template #default="{ row }">
          <el-checkbox
            :model-value="draftIdSet.has(row.id)"
            @click.stop
            @change="(v: boolean | string | number) => toggleOne(row.id, Boolean(v))"
          />
        </template>
      </el-table-column>
      <el-table-column prop="name" label="项目名称" min-width="160" show-overflow-tooltip />
      <el-table-column prop="phaseLabel" label="当前阶段" min-width="120" show-overflow-tooltip />
      <el-table-column prop="issueCount" label="问题数" width="80" align="right">
        <template #default="{ row }">{{ row.issueCount ?? 0 }}</template>
      </el-table-column>
    </el-table>

    <template #footer>
      <div class="select-footer">
        <el-button @click="emit('update:modelValue', false)">取消</el-button>
        <el-button type="primary" :disabled="!draftIds.length" @click="onSave">
          保存选择
        </el-button>
      </div>
    </template>
  </el-dialog>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';

export interface OverviewSelectProjectRow {
  id: number;
  name: string;
  phaseLabel: string;
  issueCount: number;
}

const props = defineProps<{
  modelValue: boolean;
  projects: OverviewSelectProjectRow[];
  selectedIds: number[];
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', v: boolean): void;
  (e: 'confirm', ids: number[]): void;
}>();

const draftIds = ref<number[]>([]);

const draftIdSet = computed(() => new Set(draftIds.value));

const allChecked = computed(
  () => props.projects.length > 0 && draftIds.value.length === props.projects.length,
);

const indeterminate = computed(
  () => draftIds.value.length > 0 && draftIds.value.length < props.projects.length,
);

watch(
  () => props.modelValue,
  (open) => {
    if (open) {
      draftIds.value = [...props.selectedIds];
    }
  },
);

function toggleOne(id: number, checked: boolean) {
  const set = new Set(draftIds.value);
  if (checked) set.add(id);
  else set.delete(id);
  draftIds.value = [...set];
}

function onToggleAll(val: boolean | string | number) {
  if (val) {
    draftIds.value = props.projects.map((p) => p.id);
  } else {
    draftIds.value = [];
  }
}

function onRowClick(row: OverviewSelectProjectRow) {
  toggleOne(row.id, !draftIdSet.value.has(row.id));
}

function rowClassName({ row }: { row: OverviewSelectProjectRow }) {
  return draftIdSet.value.has(row.id) ? 'is-selected-row' : '';
}

function onSave() {
  if (!draftIds.value.length) return;
  emit('confirm', [...draftIds.value]);
  emit('update:modelValue', false);
}
</script>

<style scoped lang="scss">
.select-header {
  display: flex;
  align-items: baseline;
  gap: 12px;
  flex-wrap: wrap;
  padding-right: 28px;
}

.select-title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.select-subtitle {
  margin: 0;
  font-size: 13px;
  color: #909399;
}

.select-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.select-count {
  font-size: 13px;
  color: #606266;
}

.select-table {
  width: 100%;

  :deep(.el-table__header th) {
    color: #909399;
    font-weight: 500;
    background: transparent;
  }

  :deep(.el-table__row.is-selected-row > td.el-table__cell) {
    background: #f0f7ff;
  }

  :deep(.el-table__row) {
    cursor: pointer;
  }
}

.col-head {
  font-size: 13px;
  color: #909399;
}

.select-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}
</style>

<style lang="scss">
.project-overview-select-dialog.el-dialog {
  border-radius: 10px;
}

.project-overview-select-dialog .el-dialog__header {
  padding: 16px 20px 8px;
  margin-right: 0;
}

.project-overview-select-dialog .el-dialog__body {
  padding: 8px 20px 12px;
}

.project-overview-select-dialog .el-dialog__footer {
  padding: 8px 20px 16px;
}
</style>
